import { Injectable } from '@angular/core';
import { DocumentManagerService } from './document-manager.service';
import {
    SalesDocument,
    SalesDocumentConnection,
    SalesDocumentPosition,
    DocumentAttachment,
    State,
    SalesDocumentType,
    PersonsService,
    ChangeTag,
    SalesDocumentPositionPrices,
    SalesDocumentPositionBasePrices,
    ComplaintsDocumentType,
} from '@icc/b2b/shared';
import { Store } from '@ngrx/store';
import { ConfigService, core } from '@icc/shared';
import {
    getBuyDocumentsForDocument,
    getSalesDocument,
    getBuyDocumentsForDocuments,
    getBuyPositionsForDocument,
    getDocumentAttachments,
    getSalesDocumentPositions,
    getDocumentsConnectionsForDocument,
    getSubdocuments,
    getSalesDocumentsEntities,
} from './+state/sales-documents.selectors';
import { first, firstValueFrom, of, skipWhile, tap, timeout } from 'rxjs';
import {
    addAttachment,
    addDoc,
    addPosition,
    changeDoc,
    changePosition,
    changePositionPrice,
    detachDocuments,
    joinDocuments,
    joinPositions,
    joinAttachments,
    tagDoc,
} from './+state/sales-documents.actions';
import { SyncService } from './sync.service';
import { HttpClient } from '@angular/common/http';

@Injectable({
    providedIn: 'root',
})
export class B2bDocumentManagerService extends DocumentManagerService {
    constructor(
        private store: Store<State>,
        private configService: ConfigService,
        private personsService: PersonsService,
        private syncService: SyncService,
        private http: HttpClient,
    ) {
        super();
    }

    async getDocument(documentId: string) {
        if (!documentId) {
            return null;
        }
        return this.syncService.getOrLoadDocument(documentId, getSalesDocument);
    }

    getConnectionsForDocument(documentId: string) {
        return firstValueFrom(this.store.select(getDocumentsConnectionsForDocument(documentId)));
    }

    getBuyDocuments(documentId: string) {
        return this.syncService.getOrLoadDocument(documentId, getBuyDocumentsForDocument, docs => docs.length > 0);
    }

    getBuyDocumentsForAllDocuments(documentIds: string[]) {
        return this.syncService.getOrLoadDocuments(documentIds, getBuyDocumentsForDocuments, docs => Array.from(docs.values()).every(buyDocs => buyDocs.length > 0));
    }

    async getSubdocuments(rootId: string, type: SalesDocumentType | ComplaintsDocumentType) {
        const documents = await firstValueFrom(this.store.select(getSubdocuments(rootId, type)));
        if (!documents.length) {
            const x = performance.now();
            await this.syncService.loadDocuments([rootId]);
            return await firstValueFrom(this.store.select(getSubdocuments(rootId, type)).pipe(
                timeout({
                    each: 1000,
                    with: () => of(null),
                }),
                skipWhile((doc) => !doc),
            ));
        }
        return documents;
    }

    getDocumentPosition(documentId: string) {
        return firstValueFrom(this.store.select(getSalesDocumentPositions(documentId)));
    }

    getDocumentAttachments(documentId: string) {
        return firstValueFrom(this.store.select(getDocumentAttachments(documentId)));
    }

    getBuyPositionsMap(documentId: string, all = false) {
        return this.store.select(getBuyPositionsForDocument(documentId, all)).pipe(first()).toPromise();
    }

    async getSellerUserCode(salesDocument: SalesDocument): Promise<string> {
        return (
            this.personsService.getWorkerByUserId(salesDocument.sellerUserId)?.code ??
            (salesDocument.sellerCompanyId === +this.configService.user.user.ActiveCompany
                ? this.configService.user.user.ActiveCompanyCode
                : this.configService.user.user.ActiveContractorAccountManager?.code) ??
            ''
        );
    }

    addDocument(document: SalesDocument) {
        this.store.dispatch(
            addDoc({
                doc: document,
            })
        );
    }

    addPosition(position: SalesDocumentPosition, document: SalesDocument, onlySales = false) {
        this.store.dispatch(
            addPosition({
                position,
                onlySales,
            })
        );
    }

    addAttachment(attachment: DocumentAttachment) {
        this.store.dispatch(
            addAttachment({
                attachment,
            })
        );
    }

    saveDocument(documentId: string, document: Partial<SalesDocument>, onlySales = false) {
        this.store.dispatch(
            changeDoc({
                id: documentId,
                changes: document,
                onlySales,
            })
        );
    }

    async savePosition(
        positionId: string,
        documentId: string,
        position: Partial<
            Omit<
                SalesDocumentPosition,
                keyof SalesDocumentPositionPrices | keyof SalesDocumentPositionBasePrices
            >
        >,
        onlySales = false,
    ) {
        this.store.dispatch(
            changePosition({
                id: positionId,
                docId: documentId,
                changes: position,
                onlySales,
            })
        );
    }

    async savePositionPrices(
        positionId: string,
        documentId: string,
        position: SalesDocumentPositionPrices,
    ) {
        this.store.dispatch(
            changePositionPrice({
                id: positionId,
                docId: documentId,
                changes: position,
            })
        );
    }


    joinDocuments(salesDocumentId: SalesDocument['uuid'], buyDocumentId: SalesDocument['uuid']) {
        this.store.dispatch(
            joinDocuments({
                uuid: core.generateUUID(),
                buyDocumentId,
                salesDocumentId,
            })
        );
    }

    joinPositions(
        salesDocumentId: SalesDocument['uuid'],
        buyDocumentId: SalesDocument['uuid'],
        salesPositionId: string,
        buyPositionId: string
    ) {
        if (!salesPositionId || !buyPositionId) {
            return;
        }
        this.store.dispatch(
            joinPositions({
                uuid: core.generateUUID(),
                buyDocumentId,
                salesDocumentId,
                salesPositionId,
                buyPositionId,
            })
        );
    }

    joinAttachments(
        salesDocumentId: SalesDocument['uuid'],
        buyDocumentId: SalesDocument['uuid'],
        salesAttachmentId: string,
        buyAttachmentId: string
    ) {
        if (!salesAttachmentId || !buyAttachmentId) {
            return;
        }
        this.store.dispatch(
            joinAttachments({
                uuid: core.generateUUID(),
                buyDocumentId,
                salesDocumentId,
                salesAttachmentId,
                buyAttachmentId,
            })
        );
    }

    detachDocuments(salesDocumentId: SalesDocument['uuid'], buyDocumentId: SalesDocument['uuid']) {
        this.store.dispatch(
            detachDocuments({
                buyDocumentId,
                salesDocumentId,
            })
        );
    }

    tagChanges(documentId: string, changeTag: ChangeTag) {
        this.store.dispatch(
            tagDoc({
                docId: documentId,
                changeTag,
            })
        );
    }

    /**
     * Znajduje ID najnowszego zamówienia w łańcuchu dokumentów
     */
    async findLatestOrderId(document: SalesDocument): Promise<string | null> {
        if (!document) {
            return null;
        }

        // Jeśli to zamówiona oferta, znajdź najnowsze zamówienie
        if (document.isOrderPlaced && document.salesDocumentTypeId === SalesDocumentType.Quotation) {
            // Znajdź wszystkie dokumenty potomne
            const descendants = await this.findAllDescendants(document.uuid);
            // Znajdź najnowsze zamówienie (bez aneksu)
            const latestOrder = descendants
                .filter(doc => doc.salesDocumentTypeId === SalesDocumentType.Order && !doc.isAnnexed)
                .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())[0];

            if (latestOrder) {
                return latestOrder.uuid;
            }

            // Jeśli nie znaleziono w lokalnym store, zapytaj serwer
            return await this.findLatestOrderIdFromServer(document.uuid);
        }

        // Jeśli to aneksowane zamówienie, znajdź najnowsze zamówienie w tym samym łańcuchu
        if (document.isAnnexed && document.salesDocumentTypeId === SalesDocumentType.Order) {
            // Znajdź root dokumentu
            const rootDoc = await this.findRootDocument(document);
            if (rootDoc) {
                // Znajdź wszystkie dokumenty potomne od roota
                const descendants = await this.findAllDescendants(rootDoc.uuid);
                // Znajdź najnowsze zamówienie (bez aneksu)
                const latestOrder = descendants
                    .filter(doc => doc.salesDocumentTypeId === SalesDocumentType.Order && !doc.isAnnexed)
                    .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())[0];

                if (latestOrder) {
                    return latestOrder.uuid;
                }

                // Jeśli nie znaleziono w lokalnym store, zapytaj serwer
                return await this.findLatestOrderIdFromServer(rootDoc.uuid);
            }
        }

        return null;
    }

    /**
     * Znajduje najnowsze zamówienie po stronie serwera
     */
    private async findLatestOrderIdFromServer(documentId: string): Promise<string | null> {
        try {
            const data = await this.http.post<{
                            latestOrderId: string,
                        }>(`${window.location.origin}/api/b2b/documents/${documentId}/latest-order`, {
                            userId: this.configService?.user?.user?.id,
                        }).toPromise();

            return data?.latestOrderId || null;
        } catch (error) {
            console.warn('Nie udało się pobrać najnowszego zamówienia z serwera:', error);
            return null;
        }
    }

    /**
     * Znajduje dokument root (najstarszy w łańcuchu)
     */
    private async findRootDocument(document: SalesDocument): Promise<SalesDocument | null> {
        if (!document.parentId) {
            return document;
        }

        try {
            const parentDoc = await this.getDocument(document.parentId) as SalesDocument;
            if (parentDoc) {
                return await this.findRootDocument(parentDoc);
            }
        } catch (error) {
            console.warn('Nie udało się pobrać dokumentu rodzica:', error);
        }

        return document;
    }

    /**
     * Znajduje wszystkie dokumenty potomne
     */
    private async findAllDescendants(documentId: string): Promise<SalesDocument[]> {
        try {
            // Fallback - pobierz z lokalnego store - używamy istniejące dane
            return await this.getDescendantsFromStore(documentId);
        } catch (error) {
            console.warn('Nie udało się pobrać dokumentów potomnych:', error);
            return [];
        }
    }

    /**
     * Fallback - pobiera potomków z lokalnego store
     */
    private async getDescendantsFromStore(documentId: string): Promise<SalesDocument[]> {
        try {
            // Pobierz wszystkie dokumenty ze store i znajdź potomków
            const allDocuments = await firstValueFrom(
                this.store.select(getSalesDocumentsEntities)
            );

            const descendants: SalesDocument[] = [];
            const findChildren = (parentId: string) => {
                Object.values(allDocuments).forEach(doc => {
                    if (doc && (doc as SalesDocument).parentId === parentId) {
                        const salesDoc = doc as SalesDocument;
                        descendants.push(salesDoc);
                        findChildren(salesDoc.uuid); // rekurencyjnie znajdź dzieci
                    }
                });
            };

            findChildren(documentId);
            return descendants;
        } catch (error) {
            console.warn('Nie udało się pobrać dokumentów ze store:', error);
            return [];
        }
    }
}
